import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import {
  getAccessRequestQueue,
  approveAccessRequest,
  rejectAccessRequest,
} from "../../services/accessRequestApi";
import "./AccessRequestQueue.css";

// Roles that grant visibility into external (Vestas) requests.
const EXTERNAL_VIEW_ROLES = ["idavestasapprover", "idavestasviewer"];
// Roles that grant visibility into internal (Maersk) requests.
const INTERNAL_VIEW_ROLES = ["idamaerskcx", "maerskinternal", "idamaerskbilling", "idamaerskviewer"];

const getViewerRoles = () => {
  const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
  if (!token) return [];
  try {
    const decoded = jwtDecode(token);
    let roles = decoded?.roles || [];
    if (typeof roles === "string") {
      roles = roles.split(",").map((r) => r.trim());
    }
    return roles.map((r) => r.toLowerCase());
  } catch {
    return [];
  }
};

const AccessRequestQueue = () => {
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actingId, setActingId] = useState(null);
  const [decidedIds, setDecidedIds] = useState(new Set());
  const [actionError, setActionError] = useState("");

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAccessRequestQueue();
      setRequests(data);
    } catch (err) {
      setError(err.message || "Failed to load access requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const viewerRoles = getViewerRoles();
  const canViewExternal = viewerRoles.some((r) => EXTERNAL_VIEW_ROLES.includes(r));
  const canViewInternal = viewerRoles.some((r) => INTERNAL_VIEW_ROLES.includes(r));

  console.log("🔐 [Access Request Queue visibility]", {
    viewerRoles,
    canViewExternal,
    canViewInternal,
  });

  const visibleRequests = requests.filter((req) => {
    if (req.request_type === "external") return canViewExternal;
    if (req.request_type === "internal") return canViewInternal;
    return false;
  });

  const handleDecision = async (id, decision) => {
    setActingId(id);
    setActionError("");
    try {
      if (decision === "approve") {
        await approveAccessRequest(id);
      } else {
        await rejectAccessRequest(id);
      }
      setDecidedIds((prev) => new Set(prev).add(id));
    } catch (err) {
      setActionError(err.message || `Failed to ${decision} request.`);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="arq-wrapper">
      <McTopBar
        product="Maersk | Unplanned Charges"
        productshort="Maersk"
        className="app-topbar"
      >
        <div slot="actions" className="arq-topbar-actions">
          <McButton
            appearance="neutral"
            fit="small"
            variant="ghost"
            onClick={() => navigate("/")}
          >
            Back to Home
          </McButton>
        </div>
      </McTopBar>

      <div className="arq-container mds">
        <div className="arq-header-row">
          <h3 className="arq-title">Application Access Requests</h3>
        </div>

        {actionError && <div className="arq-banner arq-banner-error">{actionError}</div>}
        {error && <div className="arq-banner arq-banner-error">{error}</div>}

        {loading ? (
          <div className="arq-empty">Loading requests…</div>
        ) : visibleRequests.length === 0 ? (
          <div className="arq-empty">No pending access requests.</div>
        ) : (
          <div className="arq-table-wrapper">
            <table className="arq-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Email</th>
                  <th>Requested Role</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRequests.map((req) => {
                  const decided = decidedIds.has(req.id);
                  const busy = actingId === req.id;
                  return (
                    <tr key={req.id} className={decided ? "arq-row-decided" : ""}>
                      <td>
                        <span className={`arq-tag arq-tag-${req.request_type}`}>
                          {req.request_type === "internal" ? "Internal" : "External"}
                        </span>
                      </td>
                      <td>{req.first_name}</td>
                      <td>{req.last_name}</td>
                      <td>{req.email}</td>
                      <td>{req.role}</td>
                      <td>{req.created_at}</td>
                      <td>
                        <div className="arq-actions">
                          <McButton
                            appearance="primary"
                            fit="small"
                            disabled={decided || busy}
                            onClick={() => handleDecision(req.id, "approve")}
                          >
                            Approve
                          </McButton>
                          <McButton
                            appearance="error"
                            fit="small"
                            variant="outlined"
                            disabled={decided || busy}
                            onClick={() => handleDecision(req.id, "reject")}
                          >
                            Reject
                          </McButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessRequestQueue;
